import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { Tabs } from '@shopify/polaris';
import { TabDescriptor } from '@shopify/polaris/build/ts/latest/src/components/Tabs/types';
import { v4 as uuidv4 } from 'uuid';
import { Container } from '@ui/container/common';
import { StyledElement } from '@ui/types';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorMessage from '@components/errors/message';
import styled from 'styled-components';
import { handleOnError } from '@utils/errors';

export type TabItem = Pick<TabDescriptor, 'content'> &
  Partial<Pick<TabDescriptor, 'accessibilityLabel'>> & {
    children: React.ReactNode;
    id?: string;
  };

export interface TabsItemDescriptor extends TabDescriptor {
  children: React.ReactNode;
}

export interface TabsComponentProps extends StyledElement {
  tabs: TabItem[];
  titleContent?: React.ReactNode;
  active?: number;
  onChangeTab?: (tabIndex: number) => void;
}

const TitleContentContainer = styled.div`
  margin-left: 1rem;
  margin-bottom: 1.25rem;
`;

const tabsGenerator = (tabs: TabItem[]): TabsItemDescriptor[] =>
  tabs.map((tab) => ({
    ...tab,
    id: tab.id || uuidv4(),
    panelID: uuidv4(),
  }));

const TabsComponent: FunctionComponent<TabsComponentProps> = ({
  active = 0,
  className,
  onChangeTab,
  tabs,
  titleContent,
}) => {
  const [selected, setSelected] = useState(active);

  const handleTabChange = useCallback(
    (selectedTabIndex) =>
      onChangeTab
        ? onChangeTab(selectedTabIndex)
        : setSelected(selectedTabIndex),
    [onChangeTab],
  );

  useEffect(() => {
    setSelected(active);
  }, [active]);

  const innerTabs = tabsGenerator(tabs);

  return (
    <>
      {titleContent && (
        <TitleContentContainer>{titleContent}</TitleContentContainer>
      )}
      <Tabs tabs={innerTabs} selected={selected} onSelect={handleTabChange}>
        <Container className={className}>
          <ErrorBoundary
            FallbackComponent={ErrorMessage}
            onError={handleOnError}
          >
            {innerTabs[selected].children}
          </ErrorBoundary>
        </Container>
      </Tabs>
    </>
  );
};

export default TabsComponent;
